import { useState, useEffect, useRef } from "react";

const STORAGE_KEY = "makeup-collection-v1";

const CATEGORIES = ["Foundation", "Concealer", "Blush", "Bronzer", "Highlighter", "Eyeshadow", "Eyeliner", "Mascara", "Lipstick", "Lip Gloss", "Lip Liner", "Setting Powder", "Primer", "Skincare", "Tools", "Other"];

const CATEGORY_COLORS = {
  "Foundation": "#D4A574", "Concealer": "#E8C5A0", "Blush": "#F4A7B9",
  "Bronzer": "#C68642", "Highlighter": "#FFD700", "Eyeshadow": "#9B7FA6",
  "Eyeliner": "#2C2C2C", "Mascara": "#1A1A1A", "Lipstick": "#C0392B",
  "Lip Gloss": "#FF69B4", "Lip Liner": "#A0522D", "Setting Powder": "#F5DEB3",
  "Primer": "#F0E6D3", "Skincare": "#7FB3D3", "Tools": "#95A5A6", "Other": "#BDC3C7"
};

const SHADES = ["N/A","Fair", "Light", "Light-Medium", "Medium", "Medium-Deep", "Deep", "Rich", "Universal"];
const FINISHES = ["N/A","Matte", "Satin", "Dewy", "Glossy", "Shimmer", "Glitter", "Natural"];

function compressImage(file) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      const MAX = 600;
      let { width, height } = img;
      if (width > height) { if (width > MAX) { height *= MAX / width; width = MAX; } }
      else { if (height > MAX) { width *= MAX / height; height = MAX; } }
      canvas.width = width; canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7).split(",")[1]);
    };
    img.src = URL.createObjectURL(file);
  });
}

async function lookupBarcode(barcode) {
  // Try Open Beauty Facts first (makeup specific)
  try {
    const r = await fetch(`https://world.openbeautyfacts.org/api/v0/product/${barcode}.json`);
    const d = await r.json();
    if (d.status === 1 && d.product) {
      const p = d.product;
      const brand = p.brands?.split(",")[0]?.trim() || "";
      const name = p.product_name || p.generic_name || "";
      if (brand || name) return { brand, name, source: "barcode" };
    }
  } catch {}
  // Fall back to Open Food Facts
  try {
    const r = await fetch(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
    const d = await r.json();
    if (d.status === 1 && d.product) {
      const p = d.product;
      const brand = p.brands?.split(",")[0]?.trim() || "";
      const name = p.product_name || "";
      if (brand || name) return { brand, name, source: "barcode" };
    }
  } catch {}
  return null;
}

async function callClaude(imageBase64, prompt) {
  const messages = imageBase64
    ? [{ role: "user", content: [
        { type: "image", source: { type: "base64", media_type: "image/jpeg", data: imageBase64 } },
        { type: "text", text: prompt }
      ]}]
    : [{ role: "user", content: prompt }];

  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, messages })
  });
  const data = await response.json();
  return data.content?.map(b => b.text || "").join("") || "";
}

export default function MakeupCollection() {
  const [collection, setCollection] = useState([]);
  const [view, setView] = useState("collection");
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState("");
  const [scannedData, setScannedData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ brand: "", name: "", category: "Lipstick", shade: "N/A", finish: "N/A", notes: "", imageBase64: null });
  const [toast, setToast] = useState(null);
  const [duplicateFound, setDuplicateFound] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [confirmPending, setConfirmPending] = useState(null);
  const videoRef = useRef();
  const canvasRef = useRef();
  const barcodeIntervalRef = useRef();
  const fileInputRef = useRef();
  const scanInputRef = useRef();
  const importInputRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const result = await window.storage.get(STORAGE_KEY);
        if (result?.value) setCollection(JSON.parse(result.value));
      } catch {}
      setLoaded(true);
    })();
  }, []);

  // Cleanup barcode scanner on unmount or view change
  useEffect(() => {
    if (view !== "barcode") stopBarcodeScanner();
  }, [view]);

  const saveCollection = async (items) => {
    setCollection(items);
    try { await window.storage.set(STORAGE_KEY, JSON.stringify(items)); } catch {}
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const checkDuplicate = (brand, name) =>
    collection.find(item =>
      item.brand?.toLowerCase() === brand?.toLowerCase() &&
      item.name?.toLowerCase() === name?.toLowerCase()
    );

  // --- BARCODE SCANNER ---
  const startBarcodeScanner = async () => {
    setView("barcode");
    setScanStatus("Starting camera…");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setScanStatus("Point at a barcode");
        startDetecting();
      }
    } catch {
      showToast("Camera not available", "error");
      setView("collection");
    }
  };

  const stopBarcodeScanner = () => {
    clearInterval(barcodeIntervalRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const startDetecting = () => {
    if (!("BarcodeDetector" in window)) {
      setScanStatus("Barcode scanner not supported on this browser — try Chrome");
      return;
    }
    const detector = new window.BarcodeDetector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39"] });
    barcodeIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) return;
      try {
        const barcodes = await detector.detect(videoRef.current);
        if (barcodes.length > 0) {
          clearInterval(barcodeIntervalRef.current);
          const code = barcodes[0].rawValue;
          setScanStatus(`Found barcode: ${code} — looking up…`);
          await handleBarcodeFound(code);
        }
      } catch {}
    }, 300);
  };

  const handleBarcodeFound = async (barcode) => {
    stopBarcodeScanner();
    setScanning(true);
    setView("scan");
    setScannedData(null);
    setDuplicateFound(null);
    try {
      setScanStatus("Looking up product…");
      const result = await lookupBarcode(barcode);
      if (result) {
        // Found in database — ask Claude to categorise it
        setScanStatus("Categorising product…");
        const raw = await callClaude(null, `You are a makeup expert. Given this product: Brand="${result.brand}", Name="${result.name}", return ONLY valid JSON:
{"brand":"${result.brand}","name":"${result.name}","category":"one of: Foundation, Concealer, Blush, Bronzer, Highlighter, Eyeshadow, Eyeliner, Mascara, Lipstick, Lip Gloss, Lip Liner, Setting Powder, Primer, Skincare, Tools, Other","shade":"N/A","finish":"N/A","notes":""}`);
        const clean = raw.replace(/```json|```/g, "").trim();
        const parsed = JSON.parse(clean);
        const dup = checkDuplicate(parsed.brand, parsed.name);
        setScannedData({ ...parsed, barcodeId: barcode });
        setDuplicateFound(dup || null);
        showToast("Found in database ⚡️");
      } else {
        // Not in database — fall back to photo scan
        setScanStatus("Not in database — taking photo for AI scan…");
        showToast("Not in database — use photo scan instead", "error");
        setView("collection");
      }
    } catch {
      showToast("Couldn't look up barcode", "error");
      setView("collection");
    }
    setScanning(false);
  };

  // --- PHOTO SCAN ---
  const handleScanImage = async (file) => {
    if (!file) return;
    setScanning(true);
    setView("scan");
    setScannedData(null);
    setDuplicateFound(null);
    setScanStatus("Analysing photo…");
    try {
      const base64 = await compressImage(file);
      const raw = await callClaude(base64, `You are a makeup product expert. Analyze this image and return ONLY valid JSON (no markdown, no preamble) with these exact fields:
{"brand":"brand name or Unknown","name":"product name","category":"one of: Foundation, Concealer, Blush, Bronzer, Highlighter, Eyeshadow, Eyeliner, Mascara, Lipstick, Lip Gloss, Lip Liner, Setting Powder, Primer, Skincare, Tools, Other","shade":"shade name if visible or N/A","finish":"one of: Matte, Satin, Dewy, Glossy, Shimmer, Glitter, Natural, N/A","notes":"any other relevant details"}`);
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      const dup = checkDuplicate(parsed.brand, parsed.name);
      setScannedData({ ...parsed, imageBase64: base64 });
      setDuplicateFound(dup || null);
    } catch (e) {
      showToast("Couldn't read product — try a clearer photo", "error");
      setView("collection");
    }
    setScanning(false);
  };

  const confirmAdd = (data, force = false) => {
    if (!force && duplicateFound) return;
    // If no duplicate, show confirmation prompt first
    if (!duplicateFound && !force) {
      setConfirmPending(data);
      return;
    }
    doAdd(data);
  };

  const doAdd = (data) => {
    const item = { ...data, id: Date.now(), addedAt: new Date().toISOString() };
    saveCollection([...collection, item]);
    setScannedData(null);
    setDuplicateFound(null);
    setConfirmPending(null);
    setView("collection");
    showToast("Added to your collection ✨");
  };

  const handleManualAdd = (force = false) => {
    if (!form.name) return showToast("Please enter a product name", "error");
    const dup = checkDuplicate(form.brand, form.name);
    if (dup && !force) { setDuplicateFound(dup); return; }
    if (!dup && !force) { setConfirmPending({ ...form }); return; }
    doAdd(form);
    setForm({ brand: "", name: "", category: "Lipstick", shade: "N/A", finish: "N/A", notes: "", imageBase64: null });
    setDuplicateFound(null);
  };

  const deleteItem = (id) => {
    saveCollection(collection.filter(i => i.id !== id));
    setSelectedItem(null);
    setView("collection");
    showToast("Removed from collection");
  };

  const exportCollection = () => {
    if (collection.length === 0) return showToast("Nothing to export yet", "error");
    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `makeup-vault-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported ${collection.length} products ✨`);
  };

  const importCollection = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        if (!Array.isArray(imported)) throw new Error("Invalid");
        const merged = [...collection];
        let added = 0;
        imported.forEach(item => {
          if (!merged.find(i => i.id === item.id)) { merged.push(item); added++; }
        });
        await saveCollection(merged);
        showToast(`Imported ${added} product${added !== 1 ? "s" : ""} ✨`);
      } catch {
        showToast("Could not read backup file", "error");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const filtered = collection.filter(item => {
    const matchCat = filterCat === "All" || item.category === filterCat;
    const matchSearch = !search || [item.brand, item.name, item.shade, item.notes].join(" ").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const usedCats = ["All", ...CATEGORIES.filter(c => collection.some(i => i.category === c))];

  const inputStyle = { width: "100%", background: "rgba(255,255,255,0.7)", border: "1px solid rgba(216,150,210,0.4)", borderRadius: 10, padding: "12px 14px", color: "#5a3060", fontSize: 14, fontFamily: "Georgia, serif", boxSizing: "border-box", outline: "none" };
  const labelStyle = { display: "block", fontSize: 10, letterSpacing: "2px", color: "#b87ab0", textTransform: "uppercase", marginBottom: 6 };
  const selectStyle = { ...inputStyle, background: "rgba(255,255,255,0.85)" };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg, #fff0f6 0%, #f3e8ff 50%, #fce4f0 100%)", color: "#5a3060", fontFamily: "Georgia, serif", position: "relative" }}>
      <div style={{ position: "fixed", inset: 0, background: "radial-gradient(ellipse at 15% 20%, rgba(255,182,217,0.35) 0%, transparent 55%), radial-gradient(ellipse at 85% 75%, rgba(196,160,230,0.3) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.4) 0%, transparent 70%)", pointerEvents: "none" }} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: toast.type === "error" ? "#ffe0e8" : "#f3e8ff", border: `1px solid ${toast.type === "error" ? "#e87090" : "#c496d8"}`, color: "#5a3060", padding: "12px 24px", borderRadius: 30, zIndex: 9999, fontSize: 13, letterSpacing: "0.5px", boxShadow: "0 8px 32px rgba(180,120,200,0.2)", animation: "fadeIn 0.2s ease", whiteSpace: "nowrap" }}>
          {toast.msg}
        </div>
      )}

      {/* CONFIRM ADD MODAL */}
      {confirmPending && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(90,48,96,0.4)", backdropFilter: "blur(6px)", zIndex: 8000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "linear-gradient(160deg, #fff0f6, #f3e8ff)", borderRadius: 24, padding: 28, width: "100%", maxWidth: 360, boxShadow: "0 20px 60px rgba(180,120,200,0.3)", animation: "fadeIn 0.2s ease" }}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 16 }}>💄</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 400, color: "#5a3060", textAlign: "center" }}>Add to collection?</h3>
            <p style={{ margin: "0 0 6px", textAlign: "center", color: "#b87ab0", fontSize: 12, letterSpacing: "1px", textTransform: "uppercase" }}>{confirmPending.brand}</p>
            <p style={{ margin: "0 0 24px", textAlign: "center", color: "#5a3060", fontSize: 16 }}>{confirmPending.name}</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmPending(null)} style={{ flex: 1, background: "rgba(255,255,255,0.6)", border: "1px solid rgba(216,150,210,0.4)", color: "#b87ab0", padding: 14, borderRadius: 12, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif" }}>
                Cancel
              </button>
              <button onClick={() => {
                const data = confirmPending;
                setConfirmPending(null);
                doAdd(data);
                if (view === "add") setForm({ brand: "", name: "", category: "Lipstick", shade: "N/A", finish: "N/A", notes: "", imageBase64: null });
              }} style={{ flex: 1, background: "linear-gradient(135deg, #f472b6, #c084fc)", border: "none", color: "#fff", padding: 14, borderRadius: 12, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif", letterSpacing: "0.5px" }}>
                Yes, add it! ✨
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header style={{ padding: "20px 20px 14px", borderBottom: "1px solid rgba(216,150,210,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(255,240,250,0.92)", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <div onClick={() => setView("collection")} style={{ cursor: "pointer" }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 400, letterSpacing: "2px", color: "#d060a8", textTransform: "uppercase" }}>Lea's Stinky Makeup 💄</h1>
          <p style={{ margin: 0, fontSize: 10, color: "#b87ab0", letterSpacing: "2px", textTransform: "uppercase" }}>{collection.length} product{collection.length !== 1 ? "s" : ""}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {view !== "collection" && (
            <button onClick={() => { setView("collection"); setScannedData(null); setDuplicateFound(null); }} style={{ background: "none", border: "1px solid rgba(216,150,210,0.5)", color: "#b87ab0", padding: "8px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, letterSpacing: "0.5px", fontFamily: "Georgia, serif" }}>← Back</button>
          )}
          {view === "collection" && (
            <>
              <button onClick={startBarcodeScanner} style={{ background: "linear-gradient(135deg, #f472b6, #c084fc)", border: "none", color: "#fff", padding: "9px 16px", borderRadius: 20, cursor: "pointer", fontSize: 12, letterSpacing: "1px", fontFamily: "Georgia, serif" }}>▦ Barcode</button>
              <button onClick={() => scanInputRef.current.click()} style={{ background: "none", border: "1px solid rgba(216,150,210,0.5)", color: "#b87ab0", padding: "9px 16px", borderRadius: 20, cursor: "pointer", fontSize: 12, letterSpacing: "0.5px", fontFamily: "Georgia, serif" }}>📷 Photo</button>
              <button onClick={() => { setDuplicateFound(null); setView("add"); }} style={{ background: "none", border: "1px solid rgba(216,150,210,0.5)", color: "#b87ab0", padding: "9px 16px", borderRadius: 20, cursor: "pointer", fontSize: 12, letterSpacing: "0.5px", fontFamily: "Georgia, serif" }}>+ Add</button>
              <button onClick={exportCollection} title="Export backup" style={{ background: "none", border: "1px solid rgba(216,150,210,0.5)", color: "#b87ab0", padding: "9px 12px", borderRadius: 20, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif" }}>⬇</button>
              <button onClick={() => importInputRef.current.click()} title="Import backup" style={{ background: "none", border: "1px solid rgba(216,150,210,0.5)", color: "#b87ab0", padding: "9px 12px", borderRadius: 20, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif" }}>⬆</button>
            </>
          )}
        </div>
      </header>

      <input ref={scanInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) { handleScanImage(e.target.files[0]); e.target.value = ""; } }} />
      <input ref={importInputRef} type="file" accept=".json" style={{ display: "none" }} onChange={importCollection} />

      {/* BARCODE SCANNER VIEW */}
      {view === "barcode" && (
        <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 500, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <video ref={videoRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} playsInline muted />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          {/* Overlay */}
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <div style={{ width: 260, height: 140, border: "2px solid #f472b6", borderRadius: 16, boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }} />
            <p style={{ color: "#f9d4e2", marginTop: 24, fontSize: 14, letterSpacing: "1px", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{scanStatus}</p>
          </div>
          {/* Close button */}
          <button onClick={() => { stopBarcodeScanner(); setView("collection"); }} style={{ position: "absolute", top: 24, right: 24, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", width: 44, height: 44, borderRadius: "50%", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
      )}

      {/* SCAN VIEW */}
      {view === "scan" && (
        <div style={{ padding: 24, display: "flex", flexDirection: "column", alignItems: "center", minHeight: "70vh", justifyContent: "center" }}>
          {scanning ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", border: "2px solid rgba(216,150,210,0.2)", borderTop: "2px solid #c084fc", animation: "spin 1s linear infinite", margin: "0 auto 24px" }} />
              <p style={{ color: "#b87ab0", letterSpacing: "3px", textTransform: "uppercase", fontSize: 12 }}>Analysing product…</p>
            </div>
          ) : scannedData && (
            <div style={{ width: "100%", maxWidth: 400, animation: "fadeIn 0.3s ease" }}>
              {duplicateFound && (
                <div style={{ background: "rgba(255,100,130,0.1)", border: "1px solid rgba(255,100,130,0.4)", borderRadius: 16, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 20 }}>⚠️</span>
                  <div>
                    <p style={{ margin: "0 0 2px", color: "#e0456a", fontSize: 13, fontWeight: 400 }}>Already in your collection!</p>
                    <p style={{ margin: 0, color: "#b87ab0", fontSize: 12 }}>{duplicateFound.brand} — {duplicateFound.name}</p>
                  </div>
                </div>
              )}
              <div style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(216,150,210,0.3)", borderRadius: 20, overflow: "hidden" }}>
                {scannedData.imageBase64 && <img src={`data:image/jpeg;base64,${scannedData.imageBase64}`} style={{ width: "100%", height: 200, objectFit: "cover" }} alt="scanned" />}
                <div style={{ padding: 20 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                    <span style={{ background: "linear-gradient(135deg, #f472b6, #c084fc)", color: "#fff", padding: "4px 12px", borderRadius: 20, fontSize: 11, letterSpacing: "1px" }}>{scannedData.category}</span>
                    {scannedData.finish && scannedData.finish !== "N/A" && <span style={{ background: "rgba(216,150,210,0.15)", border: "1px solid rgba(216,150,210,0.35)", color: "#b87ab0", padding: "4px 12px", borderRadius: 20, fontSize: 11 }}>{scannedData.finish}</span>}
                  </div>
                  <p style={{ margin: "0 0 3px", color: "#b87ab0", fontSize: 11, letterSpacing: "2px", textTransform: "uppercase" }}>{scannedData.brand}</p>
                  <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 400, color: "#5a3060" }}>{scannedData.name}</h2>
                  {scannedData.shade && scannedData.shade !== "N/A" && <p style={{ margin: "0 0 8px", color: "#d060a8", fontSize: 14 }}>Shade: {scannedData.shade}</p>}
                  {scannedData.notes && <p style={{ margin: 0, color: "#8a5090", fontSize: 13, lineHeight: 1.6 }}>{scannedData.notes}</p>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button onClick={() => { setScannedData(null); setDuplicateFound(null); setView("collection"); }} style={{ flex: 1, background: "rgba(255,255,255,0.5)", border: "1px solid rgba(216,150,210,0.3)", color: "#b87ab0", padding: 14, borderRadius: 12, cursor: "pointer", fontSize: 13, fontFamily: "Georgia, serif" }}>Cancel</button>
                <button onClick={() => duplicateFound ? doAdd(scannedData) : setConfirmPending(scannedData)} style={{ flex: 2, background: "linear-gradient(135deg, #f472b6, #c084fc)", border: "none", color: "#fff", padding: 14, borderRadius: 12, cursor: "pointer", fontSize: 13, letterSpacing: "1px", fontFamily: "Georgia, serif" }}>
                  {duplicateFound ? "Add Anyway" : "Add to Collection"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MANUAL ADD VIEW */}
      {view === "add" && (
        <div style={{ padding: "20px", maxWidth: 480, margin: "0 auto" }}>
          <h2 style={{ margin: "0 0 20px", fontWeight: 400, fontSize: 16, letterSpacing: "3px", color: "#d060a8", textTransform: "uppercase" }}>Add Product</h2>

          {duplicateFound && (
            <div style={{ background: "rgba(255,100,130,0.08)", border: "1px solid rgba(255,100,130,0.35)", borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <p style={{ margin: "0 0 8px", color: "#e0456a", fontSize: 13 }}>⚠️ You already own <strong>{duplicateFound.brand} {duplicateFound.name}</strong></p>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setDuplicateFound(null)} style={{ background: "none", border: "1px solid rgba(216,150,210,0.4)", color: "#b87ab0", padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontFamily: "Georgia, serif" }}>Edit</button>
                <button onClick={() => handleManualAdd(true)} style={{ background: "none", border: "1px solid rgba(255,100,130,0.5)", color: "#e0456a", padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontFamily: "Georgia, serif" }}>Add Anyway</button>
              </div>
            </div>
          )}

          <div onClick={() => fileInputRef.current.click()} style={{ border: "1px dashed rgba(216,150,210,0.4)", borderRadius: 14, height: form.imageBase64 ? "auto" : 100, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", marginBottom: 18, overflow: "hidden", background: "rgba(255,255,255,0.4)" }}>
            {form.imageBase64 ? <img src={`data:image/jpeg;base64,${form.imageBase64}`} style={{ width: "100%", maxHeight: 180, objectFit: "cover" }} alt="product" /> : <div style={{ textAlign: "center", color: "#b87ab0" }}><div style={{ fontSize: 28 }}>📷</div><p style={{ margin: "6px 0 0", fontSize: 12, letterSpacing: "1px" }}>Add photo (optional)</p></div>}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={async e => { if (e.target.files[0]) { const b64 = await compressImage(e.target.files[0]); setForm(f => ({ ...f, imageBase64: b64 })); } }} />

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Brand</label>
            <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Charlotte Tilbury" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Product Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Pillow Talk" style={inputStyle} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Category</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={selectStyle}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Shade</label>
              <select value={form.shade} onChange={e => setForm(f => ({ ...f, shade: e.target.value }))} style={selectStyle}>
                {SHADES.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Finish</label>
              <select value={form.finish} onChange={e => setForm(f => ({ ...f, finish: e.target.value }))} style={selectStyle}>
                {FINISHES.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 22 }}>
            <label style={labelStyle}>Shade Name / Notes</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. 3N Medium, dupes MAC Velvet Teddy…" style={inputStyle} />
          </div>

          <button onClick={() => handleManualAdd(false)} style={{ width: "100%", background: "linear-gradient(135deg, #f472b6, #c084fc)", border: "none", color: "#fff", padding: 16, borderRadius: 12, cursor: "pointer", fontSize: 13, letterSpacing: "2px", textTransform: "uppercase", fontFamily: "Georgia, serif" }}>Add to Collection</button>
        </div>
      )}

      {/* DETAIL VIEW */}
      {view === "detail" && selectedItem && (
        <div style={{ padding: "20px", maxWidth: 480, margin: "0 auto", animation: "fadeIn 0.25s ease" }}>
          {selectedItem.imageBase64
            ? <img src={`data:image/jpeg;base64,${selectedItem.imageBase64}`} style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 18, marginBottom: 22 }} alt="product" />
            : <div style={{ height: 100, background: "linear-gradient(135deg, rgba(244,114,182,0.15), rgba(192,132,252,0.15))", borderRadius: 18, marginBottom: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40 }}>💄</div>
          }
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ background: "linear-gradient(135deg, #f472b6, #c084fc)", color: "#fff", padding: "5px 14px", borderRadius: 20, fontSize: 11 }}>{selectedItem.category}</span>
            {selectedItem.finish && selectedItem.finish !== "N/A" && <span style={{ background: "rgba(216,150,210,0.15)", border: "1px solid rgba(216,150,210,0.35)", color: "#b87ab0", padding: "5px 14px", borderRadius: 20, fontSize: 11 }}>{selectedItem.finish}</span>}
          </div>
          <p style={{ margin: "0 0 3px", color: "#b87ab0", fontSize: 11, letterSpacing: "2px", textTransform: "uppercase" }}>{selectedItem.brand}</p>
          <h2 style={{ margin: "0 0 10px", fontSize: 24, fontWeight: 400, color: "#5a3060" }}>{selectedItem.name}</h2>
          {selectedItem.shade && selectedItem.shade !== "N/A" && <p style={{ margin: "0 0 8px", color: "#d060a8", fontSize: 14 }}>Shade: {selectedItem.shade}</p>}
          {selectedItem.notes && <p style={{ margin: "0 0 16px", color: "#8a5090", fontSize: 13, lineHeight: 1.7 }}>{selectedItem.notes}</p>}
          <p style={{ margin: "0 0 32px", color: "#c4a0d0", fontSize: 11, letterSpacing: "0.5px" }}>Added {new Date(selectedItem.addedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
          <button onClick={() => deleteItem(selectedItem.id)} style={{ width: "100%", background: "rgba(255,100,130,0.08)", border: "1px solid rgba(255,100,130,0.3)", color: "#e0456a", padding: 14, borderRadius: 12, cursor: "pointer", fontSize: 13, letterSpacing: "1px", fontFamily: "Georgia, serif" }}>Remove from Collection</button>
        </div>
      )}

      {/* COLLECTION VIEW */}
      {view === "collection" && (
        <div style={{ padding: "18px" }}>
          {/* Stats */}
          <div style={{ display: "flex", gap: 10, marginBottom: 18, overflowX: "auto", paddingBottom: 2 }}>
            {[
              { label: "Total", val: collection.length, emoji: "✨" },
              { label: "Lip", val: collection.filter(i => ["Lipstick","Lip Gloss","Lip Liner"].includes(i.category)).length, emoji: "💋" },
              { label: "Eye", val: collection.filter(i => ["Eyeshadow","Eyeliner","Mascara"].includes(i.category)).length, emoji: "👁" },
              { label: "Face", val: collection.filter(i => ["Foundation","Concealer","Blush","Bronzer","Highlighter","Setting Powder","Primer"].includes(i.category)).length, emoji: "✦" }
            ].map(({ label, val, emoji }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(216,150,210,0.25)", borderRadius: 14, padding: "12px 16px", textAlign: "center", minWidth: 68, flexShrink: 0 }}>
                <div style={{ fontSize: 9, color: "#b87ab0", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 4 }}>{emoji}</div>
                <div style={{ fontSize: 20, fontWeight: 400, color: "#d060a8" }}>{val}</div>
                <div style={{ fontSize: 9, letterSpacing: "1.5px", color: "#b87ab0", textTransform: "uppercase" }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Search */}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brand, name, shade…" style={{ width: "100%", background: "rgba(255,255,255,0.6)", border: "1px solid rgba(216,150,210,0.3)", borderRadius: 12, padding: "11px 14px", color: "#5a3060", fontSize: 14, fontFamily: "Georgia, serif", boxSizing: "border-box", marginBottom: 14, outline: "none" }} />

          {/* Category chips */}
          {usedCats.length > 1 && (
            <div style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 8, marginBottom: 18 }}>
              {usedCats.map(c => (
                <button key={c} onClick={() => setFilterCat(c)} style={{ flexShrink: 0, background: filterCat === c ? "linear-gradient(135deg, #f472b6, #c084fc)" : "rgba(255,255,255,0.5)", border: filterCat === c ? "none" : "1px solid rgba(216,150,210,0.3)", color: filterCat === c ? "#fff" : "#b87ab0", padding: "7px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, letterSpacing: "0.3px", fontFamily: "Georgia, serif" }}>{c}</button>
              ))}
            </div>
          )}

          {/* Grid */}
          {!loaded ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}><div style={{ width: 40, height: 40, borderRadius: "50%", border: "2px solid rgba(216,150,210,0.2)", borderTop: "2px solid #c084fc", animation: "spin 1s linear infinite", margin: "0 auto" }} /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#b87ab0" }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>💄</div>
              <p style={{ letterSpacing: "2px", textTransform: "uppercase", fontSize: 12 }}>{collection.length === 0 ? "Scan or add your first product" : "No products match"}</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
              {filtered.map(item => (
                <div key={item.id} onClick={() => { setSelectedItem(item); setView("detail"); }} style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(216,150,210,0.2)", borderRadius: 16, overflow: "hidden", cursor: "pointer", animation: "fadeIn 0.2s ease" }}>
                  {item.imageBase64
                    ? <img src={`data:image/jpeg;base64,${item.imageBase64}`} style={{ width: "100%", height: 110, objectFit: "cover" }} alt="product" />
                    : <div style={{ height: 70, background: "linear-gradient(135deg, rgba(244,114,182,0.15), rgba(192,132,252,0.15))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>💄</div>
                  }
                  <div style={{ padding: "10px 12px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: "linear-gradient(135deg, #f472b6, #c084fc)", flexShrink: 0 }} />
                      <span style={{ fontSize: 9, color: "#b87ab0", letterSpacing: "1px", textTransform: "uppercase" }}>{item.category}</span>
                    </div>
                    {item.brand && <p style={{ margin: "0 0 2px", fontSize: 10, color: "#b87ab0", letterSpacing: "0.5px" }}>{item.brand}</p>}
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.3, color: "#5a3060" }}>{item.name}</p>
                    {item.shade && item.shade !== "N/A" && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#d060a8" }}>{item.shade}</p>}
                    {item.notes && item.shade === "N/A" && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#8a5090", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* CONFIRMATION MODAL */}
      {confirmPending && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(90,48,96,0.4)", backdropFilter: "blur(6px)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "fadeIn 0.2s ease" }}>
          <div style={{ background: "linear-gradient(160deg, #fff0f6, #f3e8ff)", borderRadius: 24, padding: "28px 24px", maxWidth: 340, width: "100%", boxShadow: "0 20px 60px rgba(180,120,200,0.3)", textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>💄</div>
            <h3 style={{ margin: "0 0 8px", fontWeight: 400, fontSize: 20, color: "#5a3060" }}>Add this product?</h3>
            <p style={{ margin: "0 0 6px", color: "#b87ab0", fontSize: 11, letterSpacing: "2px", textTransform: "uppercase" }}>{confirmPending.brand}</p>
            <p style={{ margin: "0 0 20px", color: "#5a3060", fontSize: 16 }}>{confirmPending.name}</p>
            {confirmPending.shade && confirmPending.shade !== "N/A" && (
              <p style={{ margin: "-12px 0 20px", color: "#d060a8", fontSize: 13 }}>Shade: {confirmPending.shade}</p>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmPending(null)} style={{ flex: 1, background: "rgba(255,255,255,0.7)", border: "1px solid rgba(216,150,210,0.4)", color: "#b87ab0", padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif" }}>
                Cancel
              </button>
              <button onClick={() => doAdd(confirmPending)} style={{ flex: 1, background: "linear-gradient(135deg, #f472b6, #c084fc)", border: "none", color: "#fff", padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif", letterSpacing: "0.5px" }}>
                Yes, Add It!
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
